import { useEffect, useState, useRef, useCallback } from "react";
import {
  Button,
  Modal,
  Chip,
  Grid,
  Stack,
  TextField,
  Typography,
  Box,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RichTextEditor from "../RichTextEditor";
import { useAddArtist, useUpdateArtist, useDeleteArtist } from "../../hooks/useArtistMutations";
import api from "../../api/api";
import uploadImage from "../../utils/uploadImage";
import defaultPhoto from "../../assets/default.jpeg";
import toast from "react-hot-toast";
import DragAndDropList from "./DragAndDropList";
import { imageStyle, modalStyle, darkFieldSx, chipSx, addBtnSx } from "./style";

const ArtistEdit = ({ open, artistId, closeModal }) => {
  const [state, setState] = useState({});
  const [artist, setArtist] = useState({});
  const [scLinkField, setScLinkField] = useState("");
  const [ytLinkField, setYtLinkField] = useState("");
  const [genreField, setGenreField] = useState("");

  const addArtistMutation = useAddArtist();
  const updateArtistMutation = useUpdateArtist(artistId);
  const deleteArtistMutation = useDeleteArtist();

  useEffect(() => {
    if (artistId) {
      (async function () {
        try {
          const response = await api.get(`/artists/${artistId}`);
          const data = response.data?.data;
          if (data) {
            setState(data);
            setArtist(data);
          }
        } catch (error) {
          console.error("Failed to fetch artist:", error);
          toast.error("Failed to load artist");
        }
      })();
    } else {
      setState({});
      setArtist({});
    }
  }, [artistId]);

  const handleChange = useCallback((key, value) => {
    setState((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (artistId) {
      const changes = {};
      Object.keys(state).forEach((key) => {
        if (JSON.stringify(state[key]) !== JSON.stringify(artist[key])) {
          changes[key] = state[key];
        }
      });
      if (!Object.keys(changes).length) return;
      try {
        await updateArtistMutation.mutateAsync(changes);
        toast.success("Artist successfully updated");
        closeModal();
      } catch {
        toast.error("Failed to update artist");
      }
    } else {
      try {
        await addArtistMutation.mutateAsync(state);
        toast.success("Artist successfully added");
        closeModal();
      } catch {
        toast.error("Failed to add artist");
      }
    }
  }, [artist, artistId, state, addArtistMutation, updateArtistMutation, closeModal]);

  const addScLink = useCallback(() => {
    if (scLinkField) {
      setState((prev) => ({
        ...prev,
        soundcloudLinks: Array.from(new Set([...(prev.soundcloudLinks || []), scLinkField])),
      }));
      setScLinkField("");
    }
  }, [scLinkField]);

  const addYtLink = useCallback(() => {
    if (ytLinkField) {
      setState((prev) => ({
        ...prev,
        youtubeLinks: Array.from(new Set([...(prev.youtubeLinks || []), ytLinkField])),
      }));
      setYtLinkField("");
    }
  }, [ytLinkField]);

  const addGenre = useCallback(() => {
    if (genreField) {
      setState((prev) => ({
        ...prev,
        genres: Array.from(new Set([...(prev.genres || []), genreField])),
      }));
      setGenreField("");
    }
  }, [genreField]);

  const handleDeleteGenre = useCallback((genreToDelete) => {
    setState((prev) => ({
      ...prev,
      genres: prev.genres?.filter((g) => g !== genreToDelete),
    }));
  }, []);

  const handleDeleteScLink = useCallback((linkToDelete) => {
    setState((prev) => ({
      ...prev,
      soundcloudLinks: prev.soundcloudLinks?.filter((l) => l !== linkToDelete),
    }));
  }, []);

  const handleDeleteYtLink = useCallback((linkToDelete) => {
    setState((prev) => ({
      ...prev,
      youtubeLinks: prev.youtubeLinks?.filter((l) => l !== linkToDelete),
    }));
  }, []);

  const handleFileUpload = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const imageUrl = await uploadImage(file);
      if (imageUrl) setState((prev) => ({ ...prev, imageURL: imageUrl }));
    } catch {
      toast.error("Failed to upload image");
    }
  }, []);

  const handleDelete = useCallback(async () => {
    try {
      await deleteArtistMutation.mutateAsync(artistId);
      toast.success("Artist deleted successfully");
      closeModal();
    } catch {
      toast.error("Failed to delete artist");
    }
  }, [artistId, deleteArtistMutation, closeModal]);

  const imageInputRef = useRef();
  const isLoading =
    addArtistMutation.isPending ||
    updateArtistMutation.isPending ||
    deleteArtistMutation.isPending;

  return (
    <Modal open={open} onClose={closeModal}>
      <Box sx={modalStyle}>
        {/* Sticky header */}
        <Box
          sx={{
            px: { xs: 2.5, sm: 4 },
            pt: { xs: 2, sm: 2.5 },
            pb: 2,
            borderBottom: "1px solid rgba(255,255,255,0.08)",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            sx={{
              color: "white",
              fontWeight: 600,
              fontSize: { xs: "0.95rem", sm: "1.05rem" },
              letterSpacing: "0.03em",
            }}
          >
            {artistId ? "Edit Artist" : "Create Artist"}
          </Typography>
          <IconButton
            onClick={closeModal}
            size="small"
            sx={{
              color: "rgba(255,255,255,0.4)",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.07)", color: "rgba(255,255,255,0.8)" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Scrollable content */}
        <Box sx={{ px: { xs: 2.5, sm: 4 }, py: { xs: 2.5, sm: 3 }, overflowY: "auto", flex: 1 }}>
          <Grid container spacing={2} direction="column" alignItems="center">
            {/* Image upload */}
            <Grid item>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                style={{ display: "none" }}
                id="artist-upload-button"
              />
              <label htmlFor="artist-upload-button">
                <img src={state.imageURL || defaultPhoto} alt="artist" style={imageStyle} />
              </label>
              <Typography
                variant="caption"
                sx={{ display: "block", textAlign: "center", color: "rgba(255,255,255,0.3)", mt: 0.5 }}
              >
                Click image to change
              </Typography>
            </Grid>

            {/* Text fields */}
            <Grid item container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Stage Name"
                  value={state.stageName || ""}
                  fullWidth
                  sx={darkFieldSx}
                  onChange={(e) => handleChange("stageName", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="First Name"
                  value={state.name || ""}
                  fullWidth
                  sx={darkFieldSx}
                  onChange={(e) => handleChange("name", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Facebook"
                  value={state.facebook || ""}
                  fullWidth
                  sx={darkFieldSx}
                  onChange={(e) => handleChange("facebook", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Instagram"
                  value={state.instagram || ""}
                  fullWidth
                  sx={darkFieldSx}
                  onChange={(e) => handleChange("instagram", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Soundcloud"
                  value={state.soundCloud || ""}
                  fullWidth
                  sx={darkFieldSx}
                  onChange={(e) => handleChange("soundCloud", e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <RichTextEditor
                  label="Description"
                  value={state.description || ""}
                  onChange={(value) => handleChange("description", value)}
                />
              </Grid>
            </Grid>

            {/* Genre */}
            <Grid item container spacing={2} alignItems="center">
              <Grid item xs={9}>
                <TextField
                  label="Add Genre"
                  value={genreField}
                  onChange={(e) => setGenreField(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addGenre()}
                  fullWidth
                  sx={darkFieldSx}
                />
              </Grid>
              <Grid item xs={3}>
                <Button onClick={addGenre} variant="outlined" fullWidth sx={addBtnSx}>
                  Add
                </Button>
              </Grid>
            </Grid>
            <Grid item container spacing={1} direction="row" flexWrap="wrap">
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {state.genres?.map((genre) => (
                  <Chip key={genre} label={genre} sx={chipSx} onDelete={() => handleDeleteGenre(genre)} />
                ))}
              </Stack>
            </Grid>

            {/* SoundCloud links */}
            <Grid item container spacing={2} alignItems="center">
              <Grid item xs={9}>
                <TextField
                  label="Add SoundCloud Link"
                  value={scLinkField}
                  onChange={(e) => setScLinkField(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addScLink()}
                  fullWidth
                  sx={darkFieldSx}
                />
              </Grid>
              <Grid item xs={3}>
                <Button onClick={addScLink} variant="outlined" fullWidth sx={addBtnSx}>
                  Add
                </Button>
              </Grid>
            </Grid>
            <Grid item container spacing={1} direction="column" xs={12} sx={{ width: "100%", minWidth: 0 }}>
              <DragAndDropList
                listKey="soundcloudLinks"
                embedType="soundcloud"
                items={state.soundcloudLinks || []}
                handleDeleteLink={handleDeleteScLink}
                setItems={handleChange}
              />
            </Grid>

            {/* YouTube links */}
            <Grid item container spacing={2} alignItems="center">
              <Grid item xs={9}>
                <TextField
                  label="Add YouTube Link"
                  value={ytLinkField}
                  onChange={(e) => setYtLinkField(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addYtLink()}
                  fullWidth
                  sx={darkFieldSx}
                />
              </Grid>
              <Grid item xs={3}>
                <Button onClick={addYtLink} variant="outlined" fullWidth sx={addBtnSx}>
                  Add
                </Button>
              </Grid>
            </Grid>
            <Grid item container spacing={1} direction="column" xs={12} sx={{ width: "100%", minWidth: 0 }}>
              <DragAndDropList
                listKey="youtubeLinks"
                embedType="youtube"
                items={state.youtubeLinks || []}
                handleDeleteLink={handleDeleteYtLink}
                setItems={handleChange}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Sticky footer */}
        <Box
          sx={{
            px: { xs: 2.5, sm: 4 },
            py: 2,
            borderTop: "1px solid rgba(255,255,255,0.08)",
            flexShrink: 0,
            display: "flex",
            gap: 1.5,
            justifyContent: "flex-end",
          }}
        >
          {artistId && (
            <Button
              onClick={handleDelete}
              variant="outlined"
              color="error"
              disabled={isLoading}
              sx={{ borderColor: "rgba(255,80,80,0.5)", color: "rgba(255,100,100,0.9)" }}
            >
              {deleteArtistMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          )}
          <Button
            onClick={handleSubmit}
            variant="outlined"
            disabled={isLoading}
            sx={{
              borderColor: "rgba(255,255,255,0.25)",
              color: "rgba(255,255,255,0.85)",
              "&:hover": { borderColor: "white", backgroundColor: "rgba(255,255,255,0.06)", color: "white" },
              "&.Mui-disabled": { color: "rgba(255,255,255,0.2)", borderColor: "rgba(255,255,255,0.08)" },
            }}
          >
            {addArtistMutation.isPending || updateArtistMutation.isPending ? "Saving..." : "Submit"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ArtistEdit;
